import to from 'await-to-js';
import { WriteStream } from 'node:fs';
import { Readable } from 'node:stream';
import {
  ClientGrants,
  ClientGrantType,
  Component,
  ContentType,
  PagedList,
  PagedSearchList,
  Project,
  Query as QueryType,
  ZenqlQuery as ZenqlQueryType,
} from 'contensis-core-api';
import {
  Client as DeliveryApiClient,
  Entry as DeliveryEntry,
  EntrySys,
} from 'contensis-delivery-api';
import {
  Entry,
  EntryListOptions,
  ICreateTag,
  ICreateTagGroup,
  IUpdateTag,
  IUpdateTagGroup,
  Client as ManagementApiClient,
  Node,
  Query,
  Role,
  Tag,
  TagGetByLabelOptions,
  TagGroup,
  TagGroupListOptions,
  TagListOptions,
  Workflow,
} from 'contensis-management-api';
import { NodejsClient } from 'contensis-management-api/lib/client';

import { writeStream } from './FileProvider';
import {
  ApiKey,
  Block,
  BlockActionType,
  BlockVersion,
  CreateApiKeyParams,
  CreateProxyParams,
  EntryWithPublishSchedule,
  GetBlockLogsParams,
  Proxy,
  PushBlockParams,
  Renderer,
  WebhookSubscription,
} from '~/models/Contensis';
import { SearchQuery } from '~/models/SearchQuery';
import { MigrateStatus } from '~/services';
import mapErrorToObject from '~/transformations/mapAppErrorToObject';
import {
  contentTypeIdExpression,
  customWhereExpressions,
  dataFormatExpression,
  defaultExpressions,
  entryTitlesExpression,
  excludeIdsExpression,
  filterExpressions,
  includeIdsExpression,
  includePathsExpression,
  languagesExpression,
  orderByExpression,
  termExpressions,
} from '~/util/expressions';
import { assetFetch, enhancedFetch } from '~/util/fetch';
import { Logger } from '~/util/logger';
import { CachedFetch } from '~/util/lru-cache';
import { isJson, parseAlias, tryParse } from '~/util/tryparse';
import { isNumber } from '~/util/typeguards';

export type ContensisManagementApiCtor = {
  clientId?: string;
  clientSecret?: string;
  username?: string;
  password?: string;
  refreshToken?: string;
  projectId: string;
  rootUrl: string;
};

export type GetNodeOptions = Parameters<DeliveryApiClient['nodes']['get']>[0];

export type SearchResult = PagedList<Entry>;

class ContensisManagementApi {
  private assetClient: ManagementApiClient;
  private client: ManagementApiClient;
  deliveryClient!: DeliveryApiClient;
  cache: CachedFetch;
  private contensisVersion = '';

  private ensureDeliveryApiClient = async () => {
    if (this.deliveryClient) return this.deliveryClient;

    // Create a DeliveryApiClient using a bearer token from the ManagementApiClient
    const token =
      this.client.bearerToken || (await this.client.ensureBearerToken());

    this.deliveryClient = DeliveryApiClient.create({
      projectId: this.ProjectId(),
      rootUrl: this.RootUrl(),
      versionStatus: 'latest',
      // defaultHeaders: {
      //   Authorization: `Bearer ${token}`,
      // },
      fetchFn: enhancedFetch,
    });

    this.deliveryClient.bearerToken = token;
    this.deliveryClient.bearerTokenExpiryDate =
      this.client.bearerTokenExpiryDate;

    return this.deliveryClient;
  };

  get alias() {
    return parseAlias(this.client.clientConfig.rootUrl);
  }

  get deliveryApi() {
    return this.ensureDeliveryApiClient();
  }

  constructor({
    clientId = '',
    clientSecret = '',
    username,
    password,
    refreshToken,
    projectId,
    rootUrl,
  }: ContensisManagementApiCtor) {
    let credentials: {
      clientType: ClientGrantType;
      clientDetails: ClientGrants;
    };
    if (refreshToken) {
      credentials = {
        clientType: 'contensis_classic_refresh_token',
        clientDetails: {
          refreshToken,
        },
      };
    } else if (username && password) {
      credentials = {
        clientType: 'contensis_classic',
        clientDetails: {
          username,
          password,
        },
      };
    } else {
      credentials = {
        clientType: 'client_credentials',
        clientDetails: {
          clientId,
          clientSecret,
        },
      };
    }

    this.client = NodejsClient.create({
      ...credentials,
      projectId,
      rootUrl,
      fetchFn: enhancedFetch,
      responseHandler: {
        200: (res) => {
          if (res.headers.has('origin-content-api-version'))
            this.contensisVersion =
              res.headers.get('origin-content-api-version') || '';
        },
      },
    });

    // Create a separate client to load assets with a much longer request timeout
    this.assetClient = NodejsClient.create({
      ...credentials,
      projectId,
      rootUrl,
      fetchFn: assetFetch,
      responseHandler: {
        200: (res) => {
          if (res.headers.has('origin-content-api-version'))
            this.contensisVersion =
              res.headers.get('origin-content-api-version') || '';
        },
      },
    });
    this.cache = new CachedFetch(this.alias, projectId);
  }

  ContensisVersion = () => this.contensisVersion;

  BearerToken = async () =>
    this.client.bearerToken || (await this.client.ensureBearerToken());

  SecurityToken = async () => {
    if (!this.client.bearerToken) {
      await this.client.ensureBearerToken();
    }
    return (this.client as any).contensisClassicToken;
  };

  /* API KEYS */
  GetApiKeys = async () =>
    this.HttpFetch<ApiKey[]>('/api/management/security/clients?clientType=0');

  CreateApiKey = async (apiKeyParams: CreateApiKeyParams) =>
    this.HttpFetch<ApiKey>('/api/management/security/clients', {
      method: 'POST',
      body: JSON.stringify(apiKeyParams),
    });

  UpdateApiKey = async (apiKey: Partial<ApiKey>) =>
    this.HttpFetch<ApiKey>(`/api/management/security/clients/${apiKey.id}`, {
      method: 'PUT',
      body: JSON.stringify(apiKey),
    });

  DeleteApiKey = (clientId: string) =>
    this.HttpFetch(`/api/management/security/clients/${clientId}`, {
      method: 'DELETE',
    });

  /* BLOCKS */
  GetBlocks = async <T extends Block[] | Block>(blockId?: string) =>
    this.HttpFetch<T>(
      `/api/management/projects/${this.ProjectId()}/blocks${
        blockId ? `/${blockId}` : ''
      }`
    );

  GetBlockVersions = async (
    blockId: string,
    branchId = 'main', // TODO: change this to "default"
    version?: string
  ) =>
    this.HttpFetch<BlockVersion | PagedList<BlockVersion>>(
      `/api/management/projects/${this.ProjectId()}/blocks/${blockId}/branches/${branchId}/versions${
        version ? `/${version}` : '?pageSize=5'
      }`
    );

  GetBlockLogs = async ({
    blockId,
    branchId,
    dataCenter,
    includeTimestamps,
    lines,
    previous,
    sinceSeconds,
    version,
  }: GetBlockLogsParams) =>
    this.HttpFetch<string>(
      `/api/management/projects/${this.ProjectId()}/blocks/${blockId}/branches/${branchId}/versions/${version}/logs?dataCenter=${dataCenter}${
        includeTimestamps ? `&includeTimestamps=${includeTimestamps}` : ''
      }${previous ? `&previous=${previous}` : ''}${
        lines ? `&lines=${lines}` : ''
      }${sinceSeconds ? `&sinceSeconds=${sinceSeconds}` : ''}`
    );

  PushBlock = async ({ release, makeLive, ...payload }: PushBlockParams) =>
    this.HttpFetch<BlockVersion>(
      `/api/management/projects/${this.ProjectId()}/blocks?${release && !makeLive ? 'release=true' : ''}${makeLive ? 'makeLive=true' : ''}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );

  BlockAction = async (
    blockId: string,
    actionType: BlockActionType,
    version: string
  ) =>
    this.HttpFetch<BlockVersion>(
      `/api/management/projects/${this.ProjectId()}/blocks/${blockId}/actions`,
      {
        method: 'POST',
        body: JSON.stringify({
          type: actionType,
          data: {
            version,
          },
        }),
      }
    );

  /* RENDERERS */
  GetRenderers = async <T extends Renderer[] | Renderer>(rendererId?: string) =>
    this.HttpFetch<T>(
      `/api/management/projects/${this.ProjectId()}/renderers${
        rendererId ? `/${rendererId}` : ''
      }`
    );

  CreateRenderer = async (payload: Renderer) =>
    this.HttpFetch<Renderer>(
      `/api/management/projects/${this.ProjectId()}/renderers`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );

  UpdateRenderer = async (payload: Partial<Renderer>) =>
    this.HttpFetch<Renderer>(
      `/api/management/projects/${this.ProjectId()}/renderers/${payload.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );

  DeleteRenderer = (rendererId: string) =>
    this.HttpFetch(
      `/api/management/projects/${this.ProjectId()}/renderers/${rendererId}`,
      {
        method: 'DELETE',
      }
    );

  /* PROXIES */
  GetProxies = async (proxyId?: string) =>
    this.HttpFetch<PagedList<Proxy>>(
      `/api/management/projects/${this.ProjectId()}/proxies${
        proxyId ? `/${proxyId}` : ''
      }`
    );

  CreateProxy = async (payload: CreateProxyParams) =>
    this.HttpFetch<Renderer>(
      `/api/management/projects/${this.ProjectId()}/proxies`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );

  UpdateProxy = async (payload: CreateProxyParams) =>
    this.HttpFetch<WebhookSubscription>(
      `/api/management/projects/${this.ProjectId()}/proxies/${payload.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );

  // TODO: create "UpsertProxy" and "UpdateOrCreateProxy" helpers
  // that will also publish the proxy for use in migration/import scenarios
  PublishProxy = async (proxyId: string, version: string) =>
    this.HttpFetch<Proxy>(
      `/api/management/projects/${this.ProjectId()}/proxies/${proxyId}/workflow/events`,
      {
        method: 'POST',
        body: JSON.stringify({
          version,
          event: 'publish',
        }),
      }
    );

  DeleteProxy = (proxyId: string) =>
    this.HttpFetch(
      `/api/management/projects/${this.ProjectId()}/proxies/${proxyId}`,
      {
        method: 'DELETE',
      }
    );

  /* ROLES */
  GetRoles = async () => to(this.client.roles.list({ pageSize: 200 }));

  CreateRole = async (role: Role) => to(this.client.roles.create(role));

  UpdateRole = async (role: Role) => to(this.client.roles.update(role));

  DeleteRole = (roleId: string) => to(this.client.roles.delete(roleId));

  /* WORKFLOWS */
  GetWorkflows = async () =>
    this.HttpFetch<PagedList<Workflow>>(
      `/api/management/projects/${this.ProjectId()}/workflow/definitions?pageSize=100`
    );

  /* WEBHOOKS */
  GetWebhooks = async (subscriptionId?: string) =>
    this.HttpFetch<WebhookSubscription[]>(
      `/api/management/projects/${this.ProjectId()}/events/webhooks/subscriptions${
        subscriptionId ? `/${subscriptionId}` : ''
      }`
    );

  CreateWebhook = async (payload: WebhookSubscription) =>
    this.HttpFetch<WebhookSubscription>(
      `/api/management/projects/${this.ProjectId()}/events/webhooks/subscriptions`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );

  UpdateWebhook = async (payload: Partial<WebhookSubscription>) =>
    this.HttpFetch<WebhookSubscription>(
      `/api/management/projects/${this.ProjectId()}/events/webhooks/subscriptions/${
        payload.id
      }`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );

  DeleteWebhook = (subscriptionId: string) =>
    this.HttpFetch(
      `/api/management/projects/${this.ProjectId()}/events/webhooks/subscriptions/${subscriptionId}`,
      {
        method: 'DELETE',
      }
    );

  UpdateOrCreateWebhook = async (
    existing: WebhookSubscription | undefined,
    webhook: WebhookSubscription
  ): Promise<
    [AppError | null, WebhookSubscription | undefined, MigrateStatus]
  > => {
    let err: AppError | null = null;
    let savedWebhook: WebhookSubscription | undefined;
    if (!existing) {
      [err, savedWebhook] = await this.CreateWebhook(webhook);
    }
    if (existing) {
      [err, savedWebhook] = await this.UpdateWebhook({
        ...webhook,
        version: existing.version,
      });
    }
    return [err, savedWebhook, err ? 'error' : existing ? 'update' : 'create'];
  };

  /* PROJECTS */
  ProjectId = () => this.client.clientConfig.projectId;
  RootUrl = () => this.client.clientConfig.rootUrl;

  GetProjects = async () =>
    this.CacheRequest('_projects', () => this.client.projects.list(), true);

  GetProject = async (projectId: string = this.ProjectId()) =>
    this.CacheRequest(
      `_project_${projectId}`,
      () => this.client.projects.get(projectId),
      true
    );

  CreateProject = async (project: Project) =>
    this.client.projects.create(project);

  UpdateProject = async (project: Project) =>
    this.client.projects.update(project);

  DeleteProject = async (projectId: string) =>
    this.client.projects.delete(projectId);

  /* DELIVERY METHODS */
  DeliverySearchEntries = async (
    query: QueryType | ZenqlQueryType,
    linkDepth = 0
  ): Promise<PagedSearchList<DeliveryEntry>> => {
    await this.ensureDeliveryApiClient();
    return this.deliveryClient.entries.search(query, linkDepth);
  };

  DeliveryGetNodes = async (options: GetNodeOptions) => {
    await this.ensureDeliveryApiClient();
    return this.deliveryClient.nodes.get(options);
  };

  DeliveryGetNodeAncestors = async (id: string) => {
    await this.ensureDeliveryApiClient();
    return this.deliveryClient.nodes.getAncestors({ id });
  };

  DeliveryGetRootNode = async (value = 0) => {
    await this.ensureDeliveryApiClient();
    return this.deliveryClient.nodes.getRoot({ depth: value });
  };

  /* UTILITY */
  CacheRequest = <T>(id: string, fn: () => Promise<T>, noCache = false) =>
    this.cache.request(id, fn, noCache);

  GenerateCacheKey = (id: string) =>
    `${this.RootUrl()}-${this.ProjectId()}-${id}`;

  HttpFetch = async <T = any>(
    uri: string,
    init: RequestInit = {}
  ): Promise<[Error | null, T | undefined, Response | undefined]> => {
    const token = await this.BearerToken();
    const [error, response] = await to(
      enhancedFetch(`${this.RootUrl()}${uri}`, {
        ...init,
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
          ...(init.headers || {}),
        },
      })
    );

    if (response && !error) {
      const [bodyError, text] = await to(response.text());
      if (bodyError) return [bodyError, undefined, response];
      if (isJson(text)) {
        const err =
          !response.status || !response.ok ? tryParse(text) : undefined;
        const payload =
          response.status && response.ok ? tryParse(text) : undefined;
        return [err, payload, response];
      }
      return [
        response.ok
          ? null
          : new Error(text || `HTTP error ${response.status?.toString()}`),
        response.ok ? (text as unknown as T) : undefined,
        response,
      ];
    }
    return [error, undefined, response];
  };

  HttpFetchAsset = async (
    localFilePath: string,
    uri: string,
    init: RequestInit = {}
  ): Promise<[Error | null, WriteStream | undefined, Response | undefined]> => {
    const headers = {};
    // Classic token is available if we auth with username and password. It is not provided when using API key auth (client_credentials)
    const securityToken = await this.SecurityToken();
    if (securityToken)
      (headers as any).cookie = `ContensisCMSUserName=${securityToken}`;
    const [error, response] = await to(
      assetFetch(uri, {
        ...init,
        // allow init.headers.cookie to override or unset our classic token cookie header
        headers: { ...headers, ...(init.headers || {}) },
      })
    );

    if (response && !error) {
      if (response.ok && response.body) {
        const stream = Readable.from(response.body).pipe(
          writeStream(localFilePath)
        );
        return [null, stream, response];
      } else {
        const [bodyError, text] = await to(response.text());
        if (bodyError) return [bodyError, undefined, response];
        if (isJson(text)) {
          const err =
            !response.status || !response.ok ? tryParse(text) : undefined;
          const payload =
            response.status && response.ok ? tryParse(text) : undefined;
          return [err, payload, response];
        }
        return [
          response.ok ? null : new Error(text),
          response.ok ? (text as any) : undefined,
          response,
        ];
      }
    }
    return [error, undefined, response];
  };

  ShouldPublish = <T extends ContentType | Component>(
    existing: T | undefined,
    posted: T | undefined
  ) => {
    return (
      existing?.version?.versionNo !== posted?.version?.versionNo ||
      posted?.version?.versionNo.startsWith('0.') ||
      !posted?.version?.versionNo.endsWith('.0')
    );
  };

  ShouldPublishEntry = <T extends Entry>(
    existing: T | undefined,
    posted: T | undefined
  ) => {
    return (
      existing?.sys?.version?.versionNo !== posted?.sys?.version?.versionNo ||
      posted?.sys?.version?.versionNo.startsWith('0.') ||
      !posted?.sys?.version?.versionNo.endsWith('.0')
    );
  };

  ShouldScheduleEntry = <T extends EntryWithPublishSchedule>(
    entry: T | undefined
  ) => {
    return !!entry?.sys.schedules?.publish?.dateTime;
  };

  IsEntryScheduled = <T extends Entry>(entry: T | undefined) => {
    return entry?.sys?.workflow?.state === 'scheduled';
  };

  /* CONTENT TYPES */
  GetContentTypes = () =>
    this.CacheRequest(
      '_contenttypes',
      () => this.client.contentTypes.list(),
      true
    );

  GetContentType = (contentTypeId: string, version?: string) =>
    this.CacheRequest(
      `_contenttype_${contentTypeId}`,
      () => this.client.contentTypes.get({ id: contentTypeId, version }),
      true
    );

  UpsertContentType = async (
    existing: ContentType | false | undefined,
    contentType: ContentType
  ): Promise<[Error | null, ContentType | undefined, MigrateStatus]> => {
    // We can say existing is false to have this method try and find the entry
    // in cases where we cannot provide an existing entry
    if (existing === false) {
      let getError;
      [getError, existing] = await to(
        this.client.contentTypes.get(contentType.id)
      );
      if (getError && (getError as any).status !== 404)
        return [getError, existing, 'error'];
    }
    const [err, savedContentType, status] =
      await this.UpdateOrCreateContentType(existing, contentType);

    if (!err && this.ShouldPublish(existing, savedContentType)) {
      const [publishError, published] = await to(
        this.PublishContentType(savedContentType) as Promise<ContentType>
      );
      return [publishError, published, publishError ? 'error' : status];
    }
    return [err, savedContentType, err ? 'error' : status];
  };

  UpdateOrCreateContentType = async (
    existing: ContentType | undefined,
    contentType: ContentType
  ): Promise<[Error | null, ContentType | undefined, MigrateStatus]> => {
    let err: Error | null = null;
    let savedContentType: ContentType | undefined;
    if (!existing) {
      [err, savedContentType] = await to(
        this.client.contentTypes.create({
          ...contentType,
          version: undefined,
        })
      );
    }
    if (existing) {
      [err, savedContentType] = await to(
        this.client.contentTypes.update({
          ...contentType,
          previewUrl: existing.previewUrl,
          version: existing.version,
        })
      );
    }
    return [
      err,
      savedContentType,
      err ? 'error' : existing ? 'update' : 'create',
    ];
  };

  PublishContentType = <T extends Component | ContentType>(
    object: T | undefined,
    event = 'publish'
  ): Promise<Component | ContentType> => {
    if (object && 'entryTitleField' in object)
      return this.client.contentTypes.invokeWorkflow(object, event);

    return this.client.components.invokeWorkflow(object as Component, event);
  };

  DeleteContentType = (contentTypeId: string) =>
    to(this.client.contentTypes.delete(contentTypeId));

  /* COMPONENTS */
  GetComponents = () =>
    this.CacheRequest('_components', () => this.client.components.list(), true);

  GetComponent = (componentId: string, version?: string) => {
    this.CacheRequest(
      `_component_${componentId}`,
      () => this.client.components.get({ id: componentId, version }),
      true
    );
  };

  UpsertComponent = async (
    existing: Component | false | undefined,
    component: Component
  ): Promise<[Error | null, Component | undefined, MigrateStatus]> => {
    // We can say existing is false to have this method try and find the entry
    // in cases where we cannot provide an existing entry
    if (existing === false) {
      let getError;
      [getError, existing] = await to(this.client.components.get(component.id));
      if (getError && (getError as any).status !== 404)
        return [getError, existing, 'error'];
    }

    const [err, savedComponent, status] = await this.UpdateOrCreateComponent(
      existing,
      component
    );

    if (!err && this.ShouldPublish(existing, savedComponent)) {
      const [publishError, published] = await to(
        this.PublishContentType(savedComponent) as Promise<Component>
      );
      return [publishError, published, status];
    }

    return [err, savedComponent, status];
  };

  UpdateOrCreateComponent = async (
    existing: Component | undefined,
    component: Component
  ): Promise<[Error | null, Component | undefined, MigrateStatus]> => {
    let err: Error | null = null;
    let savedComponent: Component | undefined;

    if (!existing) {
      [err, savedComponent] = await to(
        this.client.components.create({
          ...component,
          version: undefined,
        })
      );
    }
    if (existing) {
      [err, savedComponent] = await to(
        this.client.components.update({
          ...component,
          version: existing.version,
        })
      );
    }
    return [
      err,
      savedComponent,
      err ? 'error' : existing ? 'update' : 'create',
    ];
  };

  DeleteComponent = (componentId: string) =>
    to(this.client.components.delete(componentId));

  /* ENTRIES */
  GetEntryById = (entryId: string) => {
    return this.client.entries.get(entryId);
  };

  GetEntry = (entry: Entry) => {
    return this.GetEntryById(entry.sys.id || '');
  };

  GetEntries = (options: EntryListOptions) => {
    return this.client.entries.list(options);
  };

  SearchEntries = (query: QueryType | ZenqlQueryType) => {
    return this.client.entries.search(query);
  };

  CreateEntry = (entry: Entry) => {
    return this.client.entries.create(entry);
  };

  UpdateEntry = (entry: Entry) => {
    return this.client.entries.update(entry);
  };

  DeleteEntry = (entryId: string, recycle = false) => {
    return this.client.entries.delete(entryId, undefined, !recycle);
  };

  PublishEntry = async (
    entry: Entry,
    publishEvent = 'publish',
    message?: any
  ) => {
    if (
      entry.sys.workflow?.state === 'draft' &&
      entry.sys.workflow.allowedEvents.includes('draft.submit')
    ) {
      const submitted = await this.client.entries.invokeWorkflow(
        entry,
        'draft.submit',
        {
          message,
        }
      );
      return await this.client.entries.invokeWorkflow(
        entry,
        `${submitted.sys.workflow?.state}.approve`,
        {
          message,
        }
      );
    } else {
      const event = entry.sys.workflow?.allowedEvents.find(
        (e) => e.endsWith(publishEvent) || e.endsWith('approve')
      );
      return await this.client.entries.invokeWorkflow(entry, `${event}`, {
        message,
      });
    }
  };

  CreateEntryPublishSchedule = (entry: EntryWithPublishSchedule) => {
    const { id, language } = entry.sys;
    const dateTimeStr = entry.sys.schedules?.publish?.dateTime;

    if (!dateTimeStr) {
      return [
        new Error(
          `[CreateEntryPublishSchedule]: Missing publish date for entry ${id}`
        ),
        undefined,
      ];
    }

    const dateTime = new Date(dateTimeStr).toISOString();
    const url = `/api/management/projects/${this.ProjectId()}/entries/${id}/schedules/publish?language=${language}`;

    /* Temporary implementation to create publish schedule with fetch, 
         as management-api package doesn't appear to implement schedules yet */
    return this.HttpFetch(url, {
      method: 'PUT',
      body: JSON.stringify({
        dateTime,
      }),
    });
  };

  ScheduleEntryPublish = async (entry: EntryWithPublishSchedule) => {
    const [scheduledError, scheduled] =
      await this.CreateEntryPublishSchedule(entry);

    if (scheduledError) {
      return [scheduledError, undefined];
    }

    const [invokedErr, invoked] = await to(
      this.client.entries.invokeWorkflow(entry, 'draft.schedule')
    );

    if (invokedErr) {
      return [invokedErr, undefined];
    }

    return [undefined, invoked];
  };

  DeleteEntryPublishSchedule = (entry: Entry) => {
    const { id, language } = entry.sys;
    const url = `/api/management/projects/${this.ProjectId()}/entries/${id}/schedules/publish?language=${language}`;

    /* Temporary implementation to delete publish schedule with fetch, 
        as management-api package doesn't appear to implement schedules yet */
    return this.HttpFetch(url, {
      method: 'DELETE',
    });
  };

  UnscheduleEntryPublish = async (entry: Entry) => {
    // We have to cancel the schedule before we can delete it
    const [invokedErr, invoked] = await to(
      this.client.entries.invokeWorkflow(entry, 'scheduled.cancel')
    );

    if (invokedErr) {
      return [invokedErr, undefined];
    }

    const [scheduledError, scheduled] =
      await this.DeleteEntryPublishSchedule(entry);

    if (scheduledError) {
      return [scheduledError, undefined];
    }

    return [undefined, scheduled];
  };

  UpsertEntry = async (
    entry: Entry,
    existing: Entry | undefined,
    publishEntry: boolean
  ): Promise<[AppError | null, Entry | undefined]> => {
    if (existing && this.IsEntryScheduled(existing)) {
      // We cannot update a scheduled entry, it has to be unscheduled first
      const [unscheduleError, unscheduled] =
        await this.UnscheduleEntryPublish(existing);
      if (unscheduleError) {
        return [unscheduleError, undefined];
      }
    }
    const [err, savedEntry] = await this.UpdateOrCreateEntry(existing, entry);
    if (savedEntry && !publishEntry)
      Logger.success(
        `[entries] [${existing ? 'updated' : 'created'}:${
          savedEntry.sys.contentTypeId
        }] '${savedEntry?.entryTitle}' | ${savedEntry.sys.id}`
      );
    if (
      !err &&
      publishEntry &&
      savedEntry &&
      this.ShouldPublishEntry(existing, savedEntry)
    ) {
      /* If entry should be published, check if we should schedule it instead.
         This is determined by the existence of the sys.schedules.publish.dateTime property.
         
         Note that schedule must be cancelled to update the entry as explained above. 
         As per current implementation, if this prop is not defined in the incoming entry,
         then the schedule will not be re-created and we will do a normal publish instead.
         */
      if (this.ShouldScheduleEntry(entry as EntryWithPublishSchedule)) {
        const modifiedSavedEntry = {
          ...savedEntry,
          sys: {
            ...savedEntry.sys,
            schedules: {
              ...(entry as EntryWithPublishSchedule).sys.schedules,
            },
          },
        };
        const [scheduleError, scheduleRes] = await to(
          this.ScheduleEntryPublish(
            modifiedSavedEntry as EntryWithPublishSchedule
          )
        );

        if (scheduleError) {
          return [scheduleError, undefined];
        } else {
          Logger.success(
            `[entries] [scheduled:${entry.sys.contentTypeId}] '${entry?.entryTitle}' | ${entry.sys.id}`
          );
        }
      } else {
        const [publishError, published] = await to(
          this.PublishEntry(savedEntry)
        );

        if (published)
          Logger.success(
            `[entries] [published:${published.sys.contentTypeId}] '${published?.entryTitle}' | ${published.sys.id}`
          );
        if (publishError) {
          return [publishError, published];
        }
        return [null, published];
      }
    }

    return [err, savedEntry];
  };

  UpdateOrCreateEntry = async (
    existing: Entry | undefined,
    entry: Entry
  ): Promise<[AppError | null, Entry | undefined]> => {
    let err: AppError | null = null;
    let savedEntry: Entry | undefined;
    if (!existing) {
      [err, savedEntry] = await to(this.client.entries.create(entry));
    }
    if (existing) {
      [err, savedEntry] = await to(
        this.client.entries.update({
          ...entry,
          sys: {
            ...existing.sys,
            metadata: {
              ...(existing.sys.metadata || {}),
              ...(entry.sys.metadata || {}),
            },
          },
        })
      );
    }
    return [err, savedEntry];
  };

  /* ASSETS */
  DownloadContentAsset = this.HttpFetchAsset;

  UpsertContentAsset = async (
    existing: Entry | false | undefined,
    asset: Entry,
    assetFilePath: string,
    parentNodePath: string
  ): Promise<[AppError | null, Entry | undefined, boolean, boolean]> => {
    if (existing === false) {
      let existingContentAssetError;
      [existingContentAssetError, existing] = await to(
        this.client.entries.get(asset.sys.id || '')
      );

      if (
        existingContentAssetError &&
        (existingContentAssetError as any).status !== 404
      )
        return [existingContentAssetError, existing, false, false];
    }
    const [err, savedAsset] = await this.UpdateOrCreateAsset(
      existing,
      asset,
      assetFilePath,
      parentNodePath
    );

    if (!err && savedAsset && this.ShouldPublishEntry(existing, savedAsset)) {
      Logger.info(
        `[entries] [publish:${savedAsset.sys.contentTypeId}] '${savedAsset?.entryTitle}' | ${savedAsset.sys.id}`
      );
      const [publishError, publishedContentAsset] = await to(
        this.PublishEntry(savedAsset)
      );
      if (publishError) {
        Logger.error(`[entries] ${mapErrorToObject(publishError).message}`);
      }
      return [publishError, publishedContentAsset, !existing, !!existing];
    }

    return [err, savedAsset, !err && !existing, !err && !!existing];
  };

  UpdateOrCreateAsset = async (
    existing: Entry | undefined,
    asset: Entry,
    assetFilePath: string,
    parentNodePath: string
  ): Promise<[AppError | null, Entry | undefined]> => {
    let err: AppError | null = null;
    let savedAsset: Entry | undefined;
    if (!existing) {
      Logger.info(
        `[assets] [create:${asset.sys.contentTypeId}] '${asset?.entryTitle}' | ${asset.sys.id}`
      );
      [err, savedAsset] = await to(
        this.assetClient.entries.createAsset(
          asset,
          assetFilePath,
          parentNodePath
        )
      );
      if (savedAsset)
        Logger.success(
          `[assets] [created:${savedAsset.sys.contentTypeId}] '${savedAsset?.entryTitle}' | ${savedAsset.sys.id}`
        );
    }
    if (existing) {
      Logger.info(
        `[assets] [update:${existing.sys.contentTypeId}] '${existing?.entryTitle}' | ${existing.sys.id}`
      );
      [err, savedAsset] = await to(
        this.assetClient.entries.updateAsset(
          { ...asset, sys: existing.sys },
          assetFilePath
        )
      );
      if (savedAsset)
        Logger.success(
          `[entries] [updated:${savedAsset.sys.contentTypeId}] '${savedAsset?.entryTitle}' | ${savedAsset.sys.id}`
        );
    }

    return [err, savedAsset];
  };

  CreateAsset = (asset: Entry, assetFilePath: string, parentNodePath: string) =>
    this.assetClient.entries.createAsset(asset, assetFilePath, parentNodePath);

  /* NODES */
  CreateNode = (node: Node) => this.client.nodes.create(node);
  UpdateNode = (node: Node) => this.client.nodes.update(node);
  GetRootNode = () => this.client.nodes.getRoot();
  GetNodeById = (nodeId: string) => this.client.nodes.get(nodeId);
  GetNodeByPath = (path: string) => this.client.nodes.get(path);
  GetNodesByEntryId = (entryId: string) =>
    this.client.nodes.getByEntryId(entryId);
  GetNodeChildren = (nodeId: string) => this.client.nodes.getChildren(nodeId);

  // UpsertNode = async (node: Node): Promise<[AppError, Node]> => {
  //   const [getError, existing] = await to(this.GetNodeById(node.id));
  //   if (getError && (getError as any).status !== 404)
  //     return [getError, existing];
  //   const [err, savedNode] = await this.UpdateOrCreateNode(existing, node);
  //   return [err, savedNode];
  // };

  UpdateOrCreateNode = async (
    existing: Node,
    node: Node
  ): Promise<[AppError | null, Node | undefined]> => {
    let err: AppError | null = null;
    let savedNode: Node | undefined;
    if (!existing) {
      [err, savedNode] = await to(this.CreateNode(node));
      if (savedNode)
        Logger.success(
          `[nodes] created '${savedNode.slug['en-GB']}' ${savedNode.id}`
        );
      if (err)
        Logger.error(
          `[nodes] Error creating node ${node.id}. Error was ${JSON.stringify(
            err
          )}`
        );
    }
    if (existing) {
      [err, savedNode] = await to(this.UpdateNode(node));
      if (savedNode)
        Logger.success(
          `[nodes] updated '${savedNode.slug['en-GB']}' ${savedNode.id}`
        );
      if (err)
        Logger.error(
          `[nodes] Error updating node ${node.id}. Error was ${JSON.stringify(
            err
          )}`
        );
    }

    return [err, savedNode];
  };

  DeleteNode = (id: string) => to(this.client.nodes.delete(id));

  /* TAGS */
  GetTag = (idOrOptions: string | TagGetByLabelOptions) =>
    to(this.client.tags.get(idOrOptions as string));
  GetTags = (options: TagListOptions = {}) =>
    to(
      this.client.tags.list({
        ...options,
      })
    );
  CreateTag = (tag: ICreateTag) => to(this.client.tags.create(tag));
  UpdateTag = (tag: IUpdateTag) => to(this.client.tags.update(tag));
  DeleteTag = (id: string) => to(this.client.tags.delete(id));
  DeleteTagsInGroup = (groupId: string, ids: string[]) =>
    to(this.client.tags.delete({ groupId, ids }));

  UpdateOrCreateTag = async (
    tag: IUpdateTag,
    existing?: Tag
  ): Promise<[AppError | null, Tag | undefined]> => {
    let err: AppError | null = null;
    let savedTag: Tag | undefined;
    if (!existing) {
      [err, savedTag] = await this.CreateTag(tag);
      if (savedTag)
        Logger.success(
          `[tags] created '${savedTag.value}' in group '${savedTag.groupId}' ${savedTag.id}`
        );
      if (err)
        Logger.error(
          `[tags] Error creating tag '${tag.value}'. Error was ${JSON.stringify(
            err
          )}`
        );
    }
    if (existing) {
      [err, savedTag] = await this.UpdateTag(tag);
      if (savedTag)
        Logger.success(
          `[tags] updated '${savedTag.value}' in group '${savedTag.groupId}' ${savedTag.id}`
        );
      if (err)
        Logger.error(
          `[tags] Error updating tag '${tag.value}' ${tag.id}. Error was ${JSON.stringify(
            err
          )}`
        );
    }

    return [err, savedTag];
  };

  /* TAG GROUPS */
  GetTagGroup = (id: string) => to(this.client.tags.groups.get(id));
  GetTagGroups = (options?: TagGroupListOptions) =>
    to(
      this.client.tags.groups.list({
        ...options,
        pageOptions: {
          ...(options?.pageOptions || {}),
          pageSize: isNumber(options?.pageOptions?.pageSize)
            ? options.pageOptions.pageSize
            : 300,
        },
      })
    );
  CreateTagGroup = (tagGroup: ICreateTagGroup) =>
    to(this.client.tags.groups.create(tagGroup));
  UpdateTagGroup = (tagGroup: IUpdateTagGroup) =>
    to(this.client.tags.groups.update(tagGroup));
  DeleteTagGroup = (id: string) => to(this.client.tags.groups.delete(id));

  UpdateOrCreateTagGroup = async (
    tagGroup: IUpdateTagGroup,
    existing?: TagGroup
  ): Promise<[AppError, undefined] | [null, TagGroup]> => {
    if (!existing) {
      const [err, savedTagGroup] = await this.CreateTagGroup(tagGroup);
      if (err) {
        Logger.error(
          `[taggroups] Error creating group '${tagGroup.id}'. Error was ${JSON.stringify(
            err
          )}`
        );
        return [err, savedTagGroup];
      } else {
        Logger.success(`[taggroups] created '${savedTagGroup.id}'`);
        return [err, savedTagGroup];
      }
    }
    const [err, savedTagGroup] = await this.UpdateTagGroup(tagGroup);
    if (err) {
      Logger.error(
        `[tags] Error updating group '${tagGroup.id}'. Error was ${JSON.stringify(
          err
        )}`
      );
      return [err, savedTagGroup];
    } else {
      Logger.success(`[tags] updated '${savedTagGroup.id}'`);
      return [err, savedTagGroup];
    }
  };
}

export const searchQuery: SearchQuery = ({
  assetTypes,
  contentTypeIds,
  dataFormats,
  entryTitles,
  excludeIds,
  fields,
  filters,
  includeArchived,
  includeDeleted,
  includeIds,
  includePaths,
  languages,
  pageSize,
  pageIndex,
  orderBy,
  searchTerm,
  versionStatus = 'published',
  webpageTemplates,
  weightedSearchFields,
  where,
}) => {
  const expressions = [
    ...defaultExpressions(versionStatus),
    ...contentTypeIdExpression(contentTypeIds, webpageTemplates, assetTypes),
    ...customWhereExpressions(where),
    ...dataFormatExpression(dataFormats),
    ...filterExpressions(filters),
    ...entryTitlesExpression(entryTitles),
    ...excludeIdsExpression(excludeIds),
    ...includeIdsExpression(includeIds),
    ...includePathsExpression(includePaths),
    ...languagesExpression(languages),
    ...termExpressions(searchTerm, weightedSearchFields),
  ];

  const query = new Query(...expressions);

  query.orderBy = orderByExpression(orderBy);

  if (fields && fields.length > 0) {
    (query as any).fields = fields;
  }

  if (includeArchived) (query as any).includeArchived = true;
  if (includeDeleted) (query as any).includeDeleted = true;

  (query as any).pageIndex = pageIndex;
  (query as any).pageSize = pageSize;
  return query as any;
};

export default ContensisManagementApi;
